import { UserAccessors, UserSelectors } from '@dtna/dvs-support-dcp-shared-state/lib/selectors/user.selector';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useHostSelector } from '../store';
import {
  getCurrentTimestamp,
  getOperatingSystem,
  getDeviceType,
  getOrGenerateTelemetrySessionID,
  getUserLocation,
  getScreenInfo,
  getBrowserDetails,
} from '../utils/telemetry';
import useFetchIpAddress from './useFetchIpAddress';
import usePostTelemetryData from './usePostTelemetryData';

export interface TelemetryData {
  timestamp: number;
  userIp: string | null;
  browserName: string;
  browserVersion: string;
  deviceType: string;
  deviceHeight: number;
  deviceWidth: number;
  operatingSystem: string;
  userLatitude?: number;
  userLongitude?: number;
  geolocationAccuracy?: number;
  sessionId: string;
  oid?: string | null;
  dcpUrl: string;
}

export const useTelemetry = () => {
  const oid = useHostSelector(UserAccessors.getUserOid);
  const isUserSignedIn = useHostSelector(UserSelectors.selectUserIsSignedIn) || false;
  const location = useLocation();
  const dcpUrl = location.pathname;
  const { userAgent } = navigator;
  const fetchIpAddress = useFetchIpAddress(isUserSignedIn);
  const postTelemetryData = usePostTelemetryData(isUserSignedIn);
  const [userIp, setUserIp] = useState(null);
  const [sessionId] = useState(getOrGenerateTelemetrySessionID());
  const [userLocation, setUserLocation] = useState<{latitude: number, longitude: number, accuracy:number} | null>(null);
  const enableUserTelemetry = process.env.REACT_APP_ENABLE_USER_TELEMETRY === 'true';

  useEffect(() => {
    if (!enableUserTelemetry) return;
    const fetchAndSetIpAddress = async () => {
      const fetchedIp = await fetchIpAddress(userIp);
      setUserIp(fetchedIp);
    };

    if (!userIp) {
      fetchAndSetIpAddress();
    }
  }, []);

  useEffect(() => {
    if (!enableUserTelemetry) return;
    const prepareTelemetryData = async () => {
      const fetchedLocation = await getUserLocation();
      setUserLocation(fetchedLocation as { latitude: number, longitude: number, accuracy: number });

      const telemetryData = {
        timestamp: getCurrentTimestamp(),
        userIp,
        browserName: getBrowserDetails(userAgent).browserName,
        browserVersion: getBrowserDetails(userAgent).browserVersion,
        deviceType: getDeviceType(userAgent),
        deviceHeight: getScreenInfo().deviceHeight,
        deviceWidth: getScreenInfo().deviceWidth,
        operatingSystem: getOperatingSystem(userAgent),
        userLatitude: userLocation?.latitude,
        userLongitude: userLocation?.longitude,
        geolocationAccuracy: userLocation?.accuracy,
        sessionId,
        oid,
        dcpUrl,
      };
      await postTelemetryData(telemetryData);
    };
    if (userIp) {
      prepareTelemetryData();
    }
  }, [dcpUrl, userIp]);
};

export default useTelemetry;
